<script lang="ts">
import type { KbdProps, TabsEmits, TabsProps, TabsSlots, TabsItem as UTabsItem } from '#ui/types';

export interface TabsItem extends UTabsItem {
	kbds?: string[] | KbdProps[];
}
</script>

<script setup lang="ts" generic="T extends TabsItem">
import type { ComponentPublicInstance } from 'vue';
import { reactiveOmit } from '@vueuse/core';
import { useForwardPropsEmits } from 'reka-ui';
import { tv } from 'tailwind-variants';
import { computed, useTemplateRef } from 'vue';
import { get, omit } from '#layers/base/utils/object';
import Tabs from '#ui/components/Tabs.vue';
import { useCollapsibleTabs } from '../composables/use-collapsible-tabs';

const props = withDefaults(defineProps<TabsProps<T> & {
	collapsible?: boolean;
}>(), {
	labelKey: 'label',
	content: true,
	collapsible: false,
});
const emit = defineEmits<TabsEmits>();
const slots = defineSlots<TabsSlots<T>>();
const forwarded = useForwardPropsEmits(reactiveOmit(props, 'collapsible') as TabsProps<T>, emit);
const proxySlots = omit(slots, ['default', 'list-trailing']);

// Render hidden content panels for aria-controls validity
const contentVariant = tv({ variants: { content: { false: 'hidden' } } });

const tabsRef = useTemplateRef<ComponentPublicInstance>('tabs');
const overflowTriggerRef = useTemplateRef<HTMLElement>('overflowTrigger');

const {
	itemsWithOverflowUi,
	overflowCount,
	hasOverflow,
	triggerMaxWidth,
	activeInOverflow,
	activeOverflowItem,
	overflowDropdownItems,
} = useCollapsibleTabs<T>({
	tabsRef,
	overflowTriggerRef,
	items: () => props.items,
	modelValue: () => props.modelValue,
	labelKey: () => props.labelKey as string,
	enabled: () => props.collapsible,
	onSelect: (value) => emit('update:modelValue', value),
});

const mergedUi = computed(() => ({
	...props.ui,
	content: contentVariant({ content: props.content, class: props.ui?.content }),
	...(props.collapsible
		? {
				root: ['min-w-0', props.ui?.root].filter(Boolean).join(' '),
				list: ['overflow-hidden', props.ui?.list].filter(Boolean).join(' '),
			}
		: {}),
	...(activeInOverflow.value ? { indicator: 'opacity-0' } : {}),
}));

const overflowTriggerStyle = computed(() => triggerMaxWidth.value !== undefined
	? { maxWidth: `${triggerMaxWidth.value}px` }
	: undefined);
</script>

<template>
	<Tabs
		ref="tabs"
		v-bind="forwarded"
		:items="itemsWithOverflowUi"
		content
		:ui="mergedUi"
	>
		<template v-for="(_, name) in proxySlots" #[name]="slotData: any">
			<!-- @vue-expect-error -->
			<slot :name v-bind="slotData" />
		</template>

		<template #default="slotData: any">
			<DTooltip :disabled="!slotData.item.kbds" :text="get(slotData.item, props.labelKey as string)" :kbds="slotData.item.kbds">
				<span>
					<slot v-bind="slotData">
						{{ get(slotData.item, props.labelKey as string) }}
					</slot>
				</span>
			</DTooltip>
		</template>

		<template v-if="collapsible" #list-trailing>
			<slot name="list-trailing" />

			<div v-if="hasOverflow" ref="overflowTrigger" class="flex shrink-0 items-center">
				<DDropdownMenu :items="overflowDropdownItems" :size="props.size">
					<DButton
						:variant="activeInOverflow ? 'outline' : 'ghost'"
						color="neutral"
						:size="props.size"
						:icon="activeOverflowItem?.icon"
						trailing-icon="lucide:chevron-down"
						:label="activeInOverflow && activeOverflowItem
							? get(activeOverflowItem, labelKey as string) as string
							: $t('common.n_more', overflowCount)"
						:style="overflowTriggerStyle"
						:ui="{
							base: 'max-w-full min-w-0',
							label: 'truncate',
							leadingIcon: 'shrink-0',
							trailingIcon: 'shrink-0',
						}"
					/>
				</DDropdownMenu>
			</div>
		</template>

		<template v-else #list-trailing>
			<slot name="list-trailing" />
		</template>
	</Tabs>
</template>
